import { atom, useAtomValue } from "jotai"
import { store } from "@/store/store"
import { photosViewAtom } from "@/store/photo/photos-view-state"
import { saveCurrentAppState } from "@/store/store-history"

export type PhotosView = "grid" | "focus" | "before/after"

// Pending view transition target (null = no transition in progress)
export const pendingViewAtom = atom<PhotosView | null>(null)

/**
 * Request a transition to a new view.
 * The target view will mount and prepare, then call commitTransition() when ready.
 */
export const requestTransition = (target: PhotosView) => {
	const current = store.get(photosViewAtom)
	if (current === target) {
		// Already on this view, no transition needed
		return
	}
	store.set(pendingViewAtom, target)
}

/**
 * Called by the target view when it's ready to be shown.
 * Moves the pending view to current and clears pending state.
 */
export const commitTransition = () => {
	const pending = store.get(pendingViewAtom)
	if (pending) {
		// Set view first, then clear pending, to keep shouldMount true throughout
		store.set(photosViewAtom, pending)
		store.set(pendingViewAtom, null)
		saveCurrentAppState(`Open ${pending} view`)
	}
}

/**
 * Cancel a pending transition (e.g., if user changes their mind).
 */
export const cancelTransition = () => {
	store.set(pendingViewAtom, null)
}

/**
 * Hook for components to coordinate view transitions.
 */
export const useViewTransition = () => {
	const currentView = useAtomValue(photosViewAtom)
	const pendingView = useAtomValue(pendingViewAtom)

	/**
	 * Returns true if the given view should be mounted.
	 * A view should mount if it's currently visible OR if it's preparing to be shown.
	 */
	const shouldMount = (view: PhotosView) => {
		return currentView === view || pendingView === view
	}

	/**
	 * Returns true if the given view is the target of a pending transition.
	 * Used by the target view to know when to prepare and then commit.
	 */
	const isTransitioningTo = (view: PhotosView) => {
		return pendingView === view
	}

	return {
		currentView,
		pendingView,
		shouldMount,
		isTransitioningTo,
		requestTransition,
		commitTransition,
		cancelTransition,
	}
}
